import { KdsDropfile } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { isAxiosError } from "axios";
import { useContext, useState } from "react";
import { Controller, FormProvider, type UseFormReturn } from "react-hook-form";
import { FileContext } from "@/components/FileUploadAsync/FileUploadAsync.context";
import { SIZE_5G } from "@/constants/fileSize.constants";
import { getDropfileErrorMessage } from "@/helpers/forms";
import { useDirectUpload } from "@/hooks/useDirectUpload";
import { type PreMediaPropositionFileFormSchema } from "./PreMediaPropositionFileForm.schema";

type ModalPreMediaPropositionFileFormDropfileProps = {
  methods: UseFormReturn<PreMediaPropositionFileFormSchema>;
};

export const ModalPreMediaPropositionFileFormDropfile = ({
  methods,
}: ModalPreMediaPropositionFileFormDropfileProps) => {
  const { t } = useTranslate();

  const [isLoading, setIsLoading] = useState(false);

  const { pushFile, setFileUploadState } = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  const handleUpload = async (file: File) => {
    try {
      const uploadDetails = await createFileUploadUrl(
        "orderItem.deliveryNotes",
        file
      );
      const newSignedId = uploadDetails?.fileSignedId;

      pushFile(newSignedId, {
        fileName: file.name,
        size: file.size,
        contentType: file.type,
      });

      await uploadFile(file, uploadDetails);

      setFileUploadState(newSignedId, "uploaded");

      methods.setValue("fileSignedId", newSignedId);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 422) {
          setFileUploadState(error.response?.data.fileSignedId, "error");
          methods.setError("fileSignedId", {
            type: "manual",
            message: t("dropfile.errorDropfileType"),
          });
          methods.setValue("fileSignedId", undefined);
          setIsLoading(false);
        }
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <Controller
        name="fileSignedId"
        render={({ field, fieldState }) => (
          <KdsDropfile
            dataCy="fileUploadForQuote"
            errorMessage={getDropfileErrorMessage(fieldState.error?.message)}
            hasError={!!fieldState.error}
            isDeletable={!!field.value}
            isReadable={!!field.value}
            isLoading={isLoading}
            maxSize={SIZE_5G}
            onDelete={() => field.onChange(undefined)}
            onUpload={(value) => void handleUpload(value)}
            onUploadError={() => methods.setValue("fileSignedId", undefined)}
            onUploadStart={() => {
              setIsLoading(true);
              methods.clearErrors("fileSignedId");
            }}
            onUploadSuccess={() => setIsLoading(false)}
            placeholderDescription={t("dropfile.placeholderMaxSize5GB")}
            placeholderTitle={t("dropfile.placeholderTitle")}
          />
        )}
      />
    </FormProvider>
  );
};
